import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {Users, FileText, Heart, Bell, Briefcase, LogOut, Loader2, AlertCircle, CheckCircle2, XCircle, Clock, ExternalLink, ShieldCheck, Sparkles, Trash2, UserCircle, MessageSquare} from 'lucide-react';
import toast from "react-hot-toast";
import Layout from "@/components/Layout";
import AccountSettings from "@/components/AccountSettings";
import Seo from "@/components/Seo";
import DashboardThemeWrapper from "@/components/DashboardThemeWrapper";
import { jobs as staticJobs } from "@/data/jobs";
import JobCard from "@/components/JobCard";
import AEDossierUploader from "@/components/AEDossierUploader";
import AvailabilityProfileForm from "@/components/AvailabilityProfileForm";
import JobAlertsManager from "@/components/JobAlertsManager";
import MobileBottomNav from "@/components/MobileBottomNav";
import { useAuth } from "@/hooks/useAuth";
import { useApplications, APPLICATION_STATUS_LABELS, type ApplicationStatus } from "@/hooks/useApplications";
import { useProDossier, type ProDossier } from "@/hooks/useProDossier";
import { useAllJobs, recruiterJobToJob } from "@/hooks/useRecruiterJobs";
import type { Job } from "@/data/jobs";
import { useFavoriteJobs } from "@/hooks/useFavoriteJobs";
import { useJobAlerts, type NewJobAlert } from "@/hooks/useJobAlerts";
import { useMessaging } from "@/hooks/useMessaging";
import { useAvailableCandidates } from "@/hooks/useAvailableCandidates";
import { PENDING_AVAILABILITY_DRAFT_KEY } from "@/hooks/useAvailabilityDepositFlow";
import { lumi } from "@/lib/lumi";
import { trackAvailabilityDepositDraftConsumed } from "@/lib/analytics";
import { checkAndNotifyJobAlerts } from "@/lib/jobAlertsService";

/** Map d'un statut de candidature vers une couleur de badge Tailwind */
const STATUS_COLORS: Record<ApplicationStatus, string> = {
  nouveau: "bg-gray-100 text-gray-700",
  en_examen: "bg-amber-100 text-amber-700",
  entretien: "bg-blue-100 text-blue-700",
  retenu: "bg-green-100 text-green-700",
  refuse: "bg-red-100 text-red-700"
};

// ─── Badge statut dossier compact (affiché dans chaque candidature AE) ─────────
interface DossierStatusBadgeProps {
  dossier: ProDossier | null;
  loading: boolean;
}

function DossierStatusBadge({ dossier, loading }: DossierStatusBadgeProps) {
  if (loading) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5">
        <Loader2 className="w-3 h-3 animate-spin" />
        Dossier…
      </span>);

  }
  if (!dossier) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5 text-blue-700 bg-blue-50 border-blue-200">
        <FileText className="w-3 h-3 flex-shrink-0" />
        À téléverser
        <a href="#dossier-ae" className="underline ml-0.5">Voir →</a>
      </span>);

  }

  const rawStatus = (dossier as unknown as Record<string, unknown>).validationStatus as string | undefined;
  const status = rawStatus === "approved" ? "approved" : rawStatus === "rejected" ? "rejected" : "pending";

  const map = {
    pending: { icon: Clock, color: "text-amber-700 bg-amber-50 border-amber-200", label: "Dossier en attente" },
    approved: { icon: CheckCircle2, color: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Dossier validé" },
    rejected: { icon: XCircle, color: "text-red-700 bg-red-50 border-red-200", label: "Dossier refusé" }
  } as const;

  const { icon: Icon, color, label } = map[status];
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${color}`}>
      <Icon className="w-3 h-3 flex-shrink-0" />
      {label}
      <a href="#dossier-ae" className="underline ml-0.5">Voir →</a>
    </span>);

}

// ─── Page Dashboard ────────────────────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile, isAuthenticated, isRecruiter, isAdmin, signOut } = useAuth();
  const {
    applications,
    loading: appsLoading,
    error: appsError,
    fetchByCandidate
  } = useApplications();

  const { fetchMine } = useProDossier();
  const { alerts } = useJobAlerts();
  const { upsert: upsertAvailability } = useAvailableCandidates();
  const { conversations: messagingConversations } = useMessaging(user?.userId);

  // Compteur de messages non lus pour le bouton « Messagerie » du hero
  const unreadMessagesCount = useMemo(() => {
    if (!user?.userId || !Array.isArray(messagingConversations)) return 0;
    return messagingConversations.reduce(
      (sum, c: {unreadCounts?: Record<string, number>;}) =>
      sum + (c?.unreadCounts?.[user.userId] || 0),
      0
    );
  }, [messagingConversations, user?.userId]);

  // ── Vérification des alertes à la connexion (best-effort, 1 fois/heure max)
  // IMPORTANT : on n'exclut PAS l'admin. L'admin (h2oxav@gmail.com) est typé
  // `isRecruiter`, mais il peut posséder des alertes candidat dans cet espace ;
  // l'exclure empêchait totalement le scan de ses alertes (aucun email envoyé).
  // On exclut donc uniquement les recruteurs « purs » (non admin).
  useEffect(() => {
    if (!isAuthenticated || !user?.userId || !user?.email) return;
    if (isRecruiter && !isAdmin) return;
    // Lance en tâche de fond, sans bloquer le rendu
    void checkAndNotifyJobAlerts({
      userId: user.userId,
      userEmail: user.email,
      userName: profile?.fullName || user.userName
    });
  }, [isAuthenticated, user?.userId, user?.email, user?.userName, isRecruiter, isAdmin, profile?.fullName]);

  const [dossier, setDossier] = useState<ProDossier | null>(null);
  const [dossierLoading, setDossierLoading] = useState(false);

  const focusDossier = searchParams.get("tab") === "dossier";
  const focusAvailability = searchParams.get("tab") === "disponible";
  const focusFavorites = searchParams.get("tab") === "favoris";
  const focusAlertes = searchParams.get("tab") === "alertes";
  const [activeTab, setActiveTab] = useState<"applications" | "disponible" | "favoris" | "alertes" | "compte">(
    focusAlertes ? "alertes" : focusFavorites ? "favoris" : focusAvailability ? "disponible" : "applications"
  );

  // Pré-remplissage éventuel d'une nouvelle alerte (depuis CTA /offres)
  const [alertDraft, setAlertDraft] = useState<Partial<NewJobAlert> | undefined>(undefined);

  // Sync tab when URL changes + lecture du draft éventuel
  useEffect(() => {
    if (focusAlertes) {
      setActiveTab("alertes");
      // Lecture du draft sauvegardé par /offres avant redirection
      try {
        const raw = sessionStorage.getItem("bnssa-mns:job-alert-draft");
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft && typeof draft === "object") {
            setAlertDraft(draft as Partial<NewJobAlert>);
          }
          sessionStorage.removeItem("bnssa-mns:job-alert-draft");
        }
      } catch {

        // silent
      }} else if (focusAvailability) setActiveTab("disponible");else
    if (focusFavorites) setActiveTab("favoris");
  }, [focusAvailability, focusFavorites, focusAlertes]);

  // ── Route protection ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/inscription", { replace: true });
    } else if (isRecruiter && !isAdmin) {
      // L'admin peut consulter n'importe quel espace : pas de redirection forcée.
      navigate("/backoffice", { replace: true });
    }
  }, [isAuthenticated, isRecruiter, isAdmin, navigate]);

  // ── Étape 2 du flux de dépôt de disponibilité (depuis /disponibles) ────────
  // Si un visiteur non connecté a rempli la pop-up de disponibilité puis créé
  // son compte, le brouillon a été stocké en sessionStorage. À la première
  // connexion candidat, on finalise automatiquement la publication du profil.
  const availabilityDraftConsumed = useRef(false);
  useEffect(() => {
    if (availabilityDraftConsumed.current) return;
    if (!isAuthenticated || !user?.userId || isRecruiter) return;

    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(PENDING_AVAILABILITY_DRAFT_KEY);
    } catch {
      return;
    }
    if (!raw) return;

    availabilityDraftConsumed.current = true;
    try {
      sessionStorage.removeItem(PENDING_AVAILABILITY_DRAFT_KEY);
    } catch {
      // silent
    }

    let draft: Record<string, unknown> | null = null;
    try {
      draft = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      draft = null;
    }
    if (!draft || typeof draft !== "object" || !draft.qualification) return;

    (async () => {
      try {
        const saved = await upsertAvailability(
          { ...(draft as Parameters<typeof upsertAvailability>[0]), creatorEmail: user.email },
          user.userId
        );
        // Best-effort : notifie les recruteurs si profil dispo immédiate publié
        if (saved && saved._id && saved.isPublished !== false && saved.isAvailableNow === true) {
          void lumi.functions
            .invoke("notifyCandidatePublished", { method: "POST", body: { candidateId: saved._id } })
            .catch((err: unknown) =>
              console.warn("[Dashboard] notifyCandidatePublished failed (non bloquant):", err)
            );
        }
        trackAvailabilityDepositDraftConsumed({
          qualification: typeof draft?.qualification === "string" ? draft.qualification : undefined,
          region: typeof draft?.region === "string" ? draft.region : undefined,
          isAvailableNow: saved?.isAvailableNow === true,
        });
        setActiveTab("disponible");
        toast.success("Votre profil est publié dans la CVthèque !");
      } catch (err) {
        console.error("[Dashboard] availability draft finalize failed:", err);
        setActiveTab("disponible");
        toast.error("Impossible de finaliser votre profil. Complétez-le ci-dessous.");
      }
    })();
  }, [isAuthenticated, user?.userId, user?.email, isRecruiter, upsertAvailability]);

  // ── Charger les candidatures réelles du candidat connecté ──────────────────
  useEffect(() => {
    if (isAuthenticated && user?.userId && !isRecruiter) {
      fetchByCandidate(user.userId);
    }
  }, [isAuthenticated, user?.userId, isRecruiter, fetchByCandidate]);

  // ── Charger le dossier pro AE s'il existe ──────────────────────────────────
  useEffect(() => {
    if (!user?.userId) return;
    let cancelled = false;
    setDossierLoading(true);
    fetchMine(user.userId, user.email).
    then((d) => {if (!cancelled) setDossier(d);}).
    catch(() => {}).
    finally(() => {if (!cancelled) setDossierLoading(false);});
    return () => {cancelled = true;};
  }, [user?.userId, user?.email, fetchMine]);

  // ── Auto-scroll vers la section dossier si ?tab=dossier ────────────────────
  useEffect(() => {
    if (!focusDossier) return;
    const t = setTimeout(() => {
      const el = document.getElementById("dossier-ae");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
    return () => clearTimeout(t);
  }, [focusDossier, dossierLoading]);

  const handleSignOut = async () => {
    await signOut();
    toast.success("Vous êtes déconnecté.");
    navigate("/", { replace: true });
  };

  // ── Contacter le recruteur depuis une candidature ─────────────────────────
  // Ouvre ou réutilise une conversation "application" puis redirige vers /messages?c=...
  const handleContactRecruiter = async (app: typeof applications[number]) => {
    if (!user?.userId || !user?.email) {
      toast.error("Vous devez être connecté.");
      return;
    }
    if (contactingAppId) return; // anti double-clic
    setContactingAppId(app._id);
    try {
      // 1. Récupère le recruteurId — soit déjà sur la candidature, soit via l'offre
      let recruiterId: string | undefined = (app as {recruiterId?: string;}).recruiterId;
      let recruiterEmail: string | undefined;
      let recruiterName: string | undefined;

      if (!recruiterId && app.jobId) {
        try {
          const job = await lumi.entities.recruiter_jobs.get(app.jobId);
          if (job) {
            recruiterId = (job as {creator?: string;}).creator;
            recruiterEmail = (job as {creatorEmail?: string;}).creatorEmail;
            recruiterName =
            (job as {creatorName?: string;}).creatorName ||
            (job as {employer?: string;}).employer;
          }
        } catch (err) {
          console.warn("[Dashboard] cannot resolve recruiter from job:", err);
        }
      }

      if (!recruiterId) {
        toast.error("Impossible de retrouver le recruteur de cette offre.");
        return;
      }
      if (recruiterId === user.userId) {
        toast.error("Vous êtes l'auteur de cette offre.");
        return;
      }

      // 2. Crée ou retrouve la conversation
      const conv = await findOrCreateConversation({
        peerUserId: recruiterId,
        peerRole: "recruiter",
        peerName: recruiterName || app.jobEmployer || "Recruteur",
        peerEmail: recruiterEmail,
        currentRole: "candidate",
        currentName: profile?.fullName || user.userName || user.email,
        currentEmail: user.email,
        context: {
          type: "application",
          applicationId: app._id,
          jobId: app.jobId,
          jobTitle: app.jobTitle
        },
        subject: `Candidature — ${app.jobTitle}`
      });

      if (!conv) {
        toast.error("Impossible d'ouvrir la conversation.");
        return;
      }

      navigate(`/messages?c=${conv._id}`);
    } catch (err) {
      console.error("[Dashboard] handleContactRecruiter failed:", err);
      toast.error("Une erreur est survenue.");
    } finally {
      setContactingAppId(null);
    }
  };

  // Détermine si le candidat a au moins une candidature Auto-Entrepreneur
  const hasAEApplication = !appsLoading && applications.some((a) => a.contractType === "auto-entrepreneur");
  // Permet au candidat d'ouvrir manuellement la création de dossier
  // depuis le bouton « Mon dossier Auto-Entrepreneur » des Actions rapides.
  const [showDossierManual, setShowDossierManual] = useState(false);
  // On affiche l'uploader complet du dossier dès qu'il est demandé via URL,
  // qu'un dossier existe, qu'au moins une candidature AE a été faite,
  // ou que le candidat a cliqué pour créer/ouvrir son dossier.
  const showDossierUploader = Boolean(focusDossier || hasAEApplication || dossier || showDossierManual);

  const handleOpenDossier = () => {
    setShowDossierManual(true);
    setActiveTab("applications");
    setTimeout(() => {
      const el = document.getElementById("dossier-ae");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const recommended = staticJobs.slice(0, 4);

  // ── Favoris ───────────────────────────────────────────────────────────────
  const { favorites, count: favoritesCount, removeFavorite, clearFavorites } = useFavoriteJobs();
  const { jobs: allJobs, loading: allJobsLoading } = useAllJobs();

  // Reconstitue les Job complets à partir des IDs sauvegardés, en préservant
  // l'ordre d'ajout (du plus récent au plus ancien : on inverse la liste).
  // Fusionne les offres BDD (recruiter_jobs) ET les offres statiques pour que
  // les favoris pointant vers des offres statiques s'affichent aussi.
  // IMPORTANT : `useAllJobs` renvoie des `RecruiterJob` bruts (clé `_id`), il
  // faut donc les convertir en `Job` (clé `id`) via `recruiterJobToJob`, sinon
  // le matching par `id` échoue et les favoris n'apparaissent jamais.
  const favoriteJobs = useMemo(() => {
    if (favorites.length === 0) return [];
    const map = new Map<string, Job>();
    // Priorité aux offres BDD (logos enrichis), puis fallback sur le static
    for (const rj of allJobs) {
      const j = recruiterJobToJob(rj);
      map.set(j.id, j);
    }
    for (const j of staticJobs) {
      if (!map.has(j.id)) map.set(j.id, j);
    }
    // Les IDs récents sont à la fin du tableau → on les remet en premier
    return [...favorites].
    reverse().
    map((id) => map.get(id)).
    filter(Boolean) as Job[];
  }, [favorites, allJobs]);

  // Offres favorites qui n'existent plus (offre supprimée/expirée côté base)
  const orphanFavoritesCount = favorites.length - favoriteJobs.length;

  const handleClearFavorites = () => {
    if (favoritesCount === 0) return;
    if (window.confirm(`Supprimer vos ${favoritesCount} offre${favoritesCount > 1 ? "s" : ""} favorite${favoritesCount > 1 ? "s" : ""} ?`)) {
      clearFavorites();
      toast.success("Favoris vidés");
    }
  };

  return (
    <Layout>
      <Seo
        title="Tableau de bord candidat — Emplois BNSSA & MNS"
        description="Gérez vos candidatures, vos offres favorites et vos alertes emploi BNSSA et MNS." />

      <DashboardThemeWrapper>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full text-xs font-semibold w-fit mb-2">
            <Users className="w-3.5 h-3.5" />
            Espace Candidat
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0 sm:flex-1">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 break-words">
                Bonjour {profile?.fullName || user?.userName || user?.email || "Candidat"}
              </h1>
              <p className="text-blue-100 break-words">Bienvenue dans votre tableau de bord.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              <Link
                  to="/messages"
                  className="relative inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                  aria-label={unreadMessagesCount > 0 ? `Messagerie (${unreadMessagesCount} non lus)` : "Messagerie"}>

                <MessageSquare className="w-4 h-4" />
                Messagerie
                {unreadMessagesCount > 0 &&
                  <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] px-1 inline-flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full ring-2 ring-blue-700">
                    {unreadMessagesCount > 99 ? "99+" : unreadMessagesCount}
                  </span>
                  }
              </Link>
              <button
                  type="button"
                  onClick={handleSignOut}
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2 rounded-lg text-sm font-semibold transition-colors">

                <LogOut className="w-4 h-4" />
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Statistiques (cachées en mobile) ─────────────────────────────── */}
      <section className="hidden md:block py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            {
              label: "Candidatures",
              value: appsLoading ? "…" : applications.length,
              icon: FileText,
              color: "bg-blue-100 text-blue-600"
            },
            { label: "Favoris", value: favoritesCount, icon: Heart, color: "bg-red-100 text-red-600" },
            { label: "Alertes actives", value: 2, icon: Bell, color: "bg-amber-100 text-amber-600" },
            { label: "Vues du profil", value: 12, icon: Users, color: "bg-green-100 text-green-600" }].
            map((s) =>
            <div key={s.label} className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-600">{s.label}</div>
              </div>
            </div>
            )}
        </div>
      </section>

      {/* ── Onglets (cachés en mobile, remplacés par la bottom nav) ─── */}
      <section className="hidden md:block bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            <button
                type="button"
                onClick={() => setActiveTab("applications")}
                className={`whitespace-nowrap flex-shrink-0 inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === "applications" ?
                "border-blue-600 text-blue-700" :
                "border-transparent text-gray-500 hover:text-gray-700"}`
                }>

              <FileText className="w-4 h-4" />
              Mes candidatures
            </button>
            <button
                type="button"
                onClick={() => setActiveTab("disponible")}
                className={`whitespace-nowrap flex-shrink-0 inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === "disponible" ?
                "border-amber-500 text-amber-700" :
                "border-transparent text-gray-500 hover:text-gray-700"}`
                }>

              <Sparkles className="w-4 h-4" />
              Mes disponibilités
            </button>
            <button
                type="button"
                onClick={() => setActiveTab("favoris")}
                className={`whitespace-nowrap flex-shrink-0 inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === "favoris" ?
                "border-red-500 text-red-700" :
                "border-transparent text-gray-500 hover:text-gray-700"}`
                }>

              <Heart className={`w-4 h-4 ${activeTab === "favoris" ? "fill-red-500" : ""}`} />
              Mes Favoris
              {favoritesCount > 0 &&
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  activeTab === "favoris" ? "bg-red-100 text-red-700" : "bg-gray-200 text-gray-700"}`
                  }>

                  {favoritesCount}
                </span>
                }
            </button>
            <button
                type="button"
                onClick={() => setActiveTab("alertes")}
                className={`whitespace-nowrap flex-shrink-0 inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === "alertes" ?
                "border-amber-500 text-amber-700" :
                "border-transparent text-gray-500 hover:text-gray-700"}`
                }>

              <Bell className={`w-4 h-4 ${activeTab === "alertes" ? "fill-amber-500" : ""}`} />
              Mes alertes
              {alerts.length > 0 &&
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  activeTab === "alertes" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-700"}`
                  }>

                  {alerts.length}
                </span>
                }
            </button>
            <button
                type="button"
                onClick={() => setActiveTab("compte")}
                className={`whitespace-nowrap flex-shrink-0 inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === "compte" ?
                "border-gray-700 text-gray-900" :
                "border-transparent text-gray-500 hover:text-gray-700"}`
                }>

              <UserCircle className="w-4 h-4" />
              Mon compte
            </button>
          </div>
        </div>
      </section>

      {/* ── Section dossier AE (uploader complet) ──────────────────────────── */}
      {activeTab === "applications" && showDossierUploader && user?.userId &&
        <section className="bg-gray-50 border-b border-gray-200 py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {dossierLoading && !dossier ?
            <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Chargement de votre dossier professionnel…
              </div> :

            <AEDossierUploader
              userId={user.userId}
              email={user.email}
              fullName={profile?.fullName || user.userName}
              phone={profile?.phone}
              initialDossier={dossier}
              onChange={(d) => setDossier(d)} />

            }
          </div>
        </section>
        }

      {/* ── Onglet Disponible Maintenant ! ─────────────────────────────── */}
      {activeTab === "disponible" && user?.userId &&
        <section className="py-10 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <AvailabilityProfileForm
              userId={user.userId}
              email={user.email}
              initialFullName={profile?.fullName || user.userName} />

          </div>
        </section>
        }

      {/* ── Contenu principal (candidatures) ─────────────────────────────── */}
      {activeTab === "applications" &&
        <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Candidatures réelles */}
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Mes candidatures
            </h2>

            {/* Chargement */}
            {appsLoading &&
              <div className="flex items-center gap-2 text-gray-500 py-6">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Chargement de vos candidatures…</span>
              </div>
              }

            {/* Erreur */}
            {!appsLoading && appsError &&
              <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl p-4 text-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                {appsError}
              </div>
              }

            {/* Aucune candidature */}
            {!appsLoading && !appsError && applications.length === 0 &&
              <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-8 text-center">
                <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">Aucune candidature pour l'instant.</p>
                <p className="text-xs text-gray-400 mt-1">Vos candidatures soumises apparaîtront ici.</p>
                <Link
                  to="/offres"
                  className="inline-block mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">

                  Voir les offres
                </Link>
              </div>
              }

            {/* Liste des candidatures */}
            {!appsLoading && !appsError && applications.length > 0 &&
              <div className="space-y-4">
                {applications.map((a) => {
                  const status = a.status as ApplicationStatus;
                  const badgeColor = STATUS_COLORS[status] ?? "bg-gray-100 text-gray-700";
                  const label = APPLICATION_STATUS_LABELS[status] ?? status;
                  const isAE = a.contractType === "auto-entrepreneur";

                  return (
                    <div
                      key={a._id}
                      className="bg-white border border-gray-200 rounded-xl p-4 overflow-hidden">

                      <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
                        <div className="flex-1 min-w-0 w-full">
                          <h3 className="font-semibold text-gray-900 truncate">{a.jobTitle}</h3>
                          {a.jobId &&
                          <a
                            href={`/offre/${a.jobId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 hover:underline mt-1">

                              Voir l'offre
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          }
                          <p className="text-xs text-gray-500 mt-0.5 break-words">
                            {a.jobEmployer && <span>{a.jobEmployer} · </span>}
                            {a.jobCategory &&
                            <span
                              className={`font-medium ${a.jobCategory === "BNSSA" ? "text-red-600" : "text-blue-600"}`}>

                                {a.jobCategory}
                              </span>
                            }
                            {a.contractType && <span className="text-gray-400"> · {a.contractType}</span>}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            Postulé le {new Date(a.createdAt).toLocaleDateString("fr-FR", {
                              day: "numeric",
                              month: "long",
                              year: "numeric"
                            })}
                            {typeof a.proposedRate === "number" &&
                            <span className="ml-2 font-medium text-indigo-600">
                                · Tarif proposé : {a.proposedRate} €/h
                              </span>
                            }
                          </p>
                        </div>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap flex-shrink-0 ${badgeColor}`}>
                          {label}
                        </span>
                      </div>

                      {/* Pour les candidatures AE : badge compact avec lien vers la section dossier */}
                      {isAE &&
                      <div className="mt-2.5 pt-2.5 border-t border-gray-100 flex items-center gap-2">
                          <ShieldCheck className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-[11px] text-gray-500">Dossier professionnel :</span>
                          <DossierStatusBadge dossier={dossier} loading={dossierLoading} />
                        </div>
                      }

                    </div>);

                })}
              </div>
              }
          </div>

          {/* Actions rapides */}
          <aside>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Actions rapides</h2>
            <div className="space-y-3">
              {/* Bouton Mon dossier Auto-Entrepreneur — toujours visible, premier de la liste.
                     Si aucun dossier n'existe encore, le clic ouvre l'uploader pour créer le dossier. */}
              {(() => {
                  const dossierStatus = dossier ?
                  (dossier as unknown as Record<string, unknown>).validationStatus as string | undefined :
                  undefined;
                  const subtitle = !dossier ?
                  "Créer mon dossier Auto-Entrepreneur" :
                  dossierStatus === "approved" ?
                  "✓ Dossier validé" :
                  dossierStatus === "rejected" ?
                  "⚠ Action requise" :
                  "En attente de validation";
                  const stateClass = !dossier ?
                  "bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white border-transparent" :
                  dossierStatus === "approved" ?
                  "bg-white border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50 text-gray-900" :
                  dossierStatus === "rejected" ?
                  "bg-white border-red-200 hover:border-red-300 hover:bg-red-50 text-gray-900" :
                  "bg-white border-amber-200 hover:border-amber-300 hover:bg-amber-50 text-gray-900";
                  const subtitleClass = !dossier ?
                  "text-emerald-50" :
                  dossierStatus === "approved" ?
                  "text-emerald-700" :
                  dossierStatus === "rejected" ?
                  "text-red-600" :
                  "text-amber-700";
                  const iconClass = !dossier ? "text-white" : "text-gray-600";
                  return (
                    <button
                      type="button"
                      onClick={handleOpenDossier}
                      className={`w-full text-left block p-4 rounded-xl font-semibold transition-colors border ${stateClass}`}>

                    <ShieldCheck className={`w-5 h-5 mb-2 ${iconClass}`} />
                    <span className="block">Mon dossier Auto-Entrepreneur</span>
                    <p className={`text-xs font-normal mt-1 ${subtitleClass}`}>
                      {dossierLoading && !dossier ? "Chargement…" : subtitle}
                    </p>
                  </button>);

                })()}

              <Link
                  to="/offres"
                  className="block bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-xl font-semibold transition-colors">

                <Briefcase className="w-5 h-5 mb-2" />
                Voir les offres
                <p className="text-xs text-blue-100 font-normal mt-1">Trouvez votre prochain poste</p>
              </Link>
              <Link
                  to="/formations"
                  className="block bg-amber-600 hover:bg-amber-700 text-white p-4 rounded-xl font-semibold transition-colors">

                <Users className="w-5 h-5 mb-2" />
                Catalogue formations
                <p className="text-xs text-amber-100 font-normal mt-1">Développez vos compétences</p>
              </Link>

              {/* Rappel si dossier refusé */}
              {dossier && (() => {
                  const raw = (dossier as unknown as Record<string, unknown>).validationStatus as string | undefined;
                  if (raw !== "rejected") return null;
                  return (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-xs font-semibold text-amber-800 mb-1">Dossier Auto-Entrepreneur refusé ?</p>
                    <p className="text-[11px] text-amber-700">
                      Vous pouvez postuler aux offres CDD, CDI, intérim, etc. sans restriction.
                    </p>
                    <Link
                        to="/offres"
                        className="inline-block mt-2 text-[11px] font-bold text-amber-800 underline">

                      Voir toutes les offres →
                    </Link>
                  </div>);

                })()}
            </div>
          </aside>
        </div>
      </section>
        }

      {/* ── Onglet Mes alertes ──────────────────────────────────────────── */}
      {activeTab === "alertes" && user?.userId &&
        <section className="py-10 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <JobAlertsManager
              userId={user.userId}
              userEmail={user.email}
              initialDraft={alertDraft} />

          </div>
        </section>
        }

      {/* ── Onglet Mon compte ────────────────────────────────────────────── */}
      {activeTab === "compte" &&
        <section className="py-10 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AccountSettings email={user?.email || ""} accent="blue" />
          </div>
        </section>
        }

      {/* ── Onglet Mes Favoris ───────────────────────────────────────────── */}
      {activeTab === "favoris" &&
        <section className="py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                  Mes offres favorites
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {favoritesCount === 0 ?
                  "Vous n'avez sauvegardé aucune offre pour le moment." :
                  `${favoritesCount} offre${favoritesCount > 1 ? "s" : ""} sauvegardée${favoritesCount > 1 ? "s" : ""} sur cet appareil.`}
                </p>
              </div>
              {favoritesCount > 0 &&
              <button
                type="button"
                onClick={handleClearFavorites}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-red-700 border border-gray-200 hover:border-red-200 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors">

                  <Trash2 className="w-3.5 h-3.5" />
                  Vider mes favoris
                </button>
              }
            </div>

            {/* Loading */}
            {allJobsLoading && favoritesCount > 0 && favoriteJobs.length === 0 &&
            <div className="flex items-center gap-2 text-gray-500 py-6">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Chargement de vos offres favorites…</span>
              </div>
            }

            {/* État vide */}
            {!allJobsLoading && favoritesCount === 0 &&
            <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-10 text-center">
                <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-700 font-semibold">Aucune offre favorite</p>
                <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
                  Cliquez sur le cœur en haut à droite d'une offre pour l'ajouter à vos favoris. Vos sélections sont conservées sur cet appareil.
                </p>
                <Link
                to="/offres"
                className="inline-flex items-center gap-2 mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">

                  <Briefcase className="w-4 h-4" />
                  Parcourir les offres
                </Link>
              </div>
            }

            {/* Grille des favoris */}
            {favoriteJobs.length > 0 &&
            <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {favoriteJobs.map((j) =>
                <JobCard key={j.id} job={j} />
                )}
                </div>

                {orphanFavoritesCount > 0 &&
              <div className="mt-6 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      {orphanFavoritesCount} offre{orphanFavoritesCount > 1 ? "s" : ""} favorite{orphanFavoritesCount > 1 ? "s" : ""} n'{orphanFavoritesCount > 1 ? "existent" : "existe"} plus (supprimée{orphanFavoritesCount > 1 ? "s" : ""} ou expirée{orphanFavoritesCount > 1 ? "s" : ""}).{" "}
                      <button
                    type="button"
                    onClick={() => {
                      const validIds = favoriteJobs.map((j) => j.id);
                      favorites.forEach((id) => {
                        if (!validIds.includes(id)) removeFavorite(id);
                      });
                      toast.success("Favoris nettoyés");
                    }}
                    className="underline font-semibold hover:text-amber-900">

                        Nettoyer
                      </button>
                    </span>
                  </div>
              }
              </>
            }
          </div>
        </section>
        }

      {/* ── Offres recommandées ──────────────────────────────────────────── */}
      {activeTab === "applications" &&
        <section className="py-10 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Offres recommandées pour vous</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {recommended.map((j) =>
              <JobCard key={j.id} job={j} />
              )}
          </div>
        </div>
      </section>
        }

      {/* ── Bottom navigation mobile-only ─────────────────────────────── */}
      <MobileBottomNav
          ariaLabel="Navigation candidat"
          accent="blue"
          active={activeTab}
          onChange={(id) => setActiveTab(id)}
          items={[
          { id: "applications", label: "Candidatures", icon: FileText },
          { id: "disponible", label: "Mes disponibilités", icon: Sparkles },
          { id: "favoris", label: "Favoris", icon: Heart, badge: favoritesCount },
          { id: "alertes", label: "Alertes", icon: Bell, badge: alerts.length },
          { id: "compte", label: "Compte", icon: UserCircle }]
          } />

      {/* Espace réservé pour la bottom nav mobile, neutre en desktop */}
      <div className="h-16 md:h-0" aria-hidden="true" />
      </DashboardThemeWrapper>
    </Layout>);

}